// react
import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';

// formik
import { Field, FieldProps, Form, useFormikContext } from 'formik';

// components
import Loader from 'shared/components/Loader';

import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import { DatePicker } from 'shared/components/Pickers/NewUI/DatePicker';
import InputErrorHint from 'shared/components/InputErrorHint';
import { TimeLimit } from 'shared/components/TimeLimit';
import Input from 'shared/components/Inputs/Input';
import Editor from 'shared/components/Editor';
import ItemGallery from '../UploadSeveralPhotos';
import { HashtagField } from '../HashtagField';

// types
import { IHashtag } from 'redux/hashtag-service/types';

// interfaces
import { FormikOpportunityMainForm } from './interfaces';

// styles
import styles from './index.module.scss';
import Select from '../../../../../shared/components/Inputs/Select';
import { SelectOption } from '../../../../../shared/components/Inputs/Select/types';

interface IEditManualOppFormProps {
	isLoading: boolean;
	organizations: Array<SelectOption>;
	options: Array<IHashtag>;
	isVisibleHashtagField?: boolean;
	setIsVisibleHashtagField?: Dispatch<SetStateAction<boolean>>;
	isPreviousCompleted?: boolean;
}

export const EditManualOppForm: FC<IEditManualOppFormProps> = ({
	options,
	organizations,
	isLoading,
	isVisibleHashtagField,
	setIsVisibleHashtagField,
	isPreviousCompleted,
}) => {
	const { dirty, errors, touched, setFieldValue, values }: FormikOpportunityMainForm = useFormikContext();

	useEffect(() => {
		if (!isVisibleHashtagField) {
			setFieldValue('tagId', null);
		}
	}, [values, isVisibleHashtagField]);

	// eslint-disable-next-line no-unused-vars
	const handleChangeVisibleHashtag = (checked: boolean, _value?: number | undefined) => {
		setIsVisibleHashtagField?.(checked);
	};

	return (
		<Form className={styles.form} title="Edit Chesed Opportunity">
			<Field
				name="organisationId"
				options={organizations}
				component={Select}
				isOrganization={true}
				label="Associated with"
				placeholder="Select Organization / School"
				parentClassName={styles.organizations}
				selectBaseClassName={styles.selectBase}
				isRequired={true}
			/>
			<Field
				component={Input}
				label="Opportunity name:*"
				placeholder="Opportunity name"
				name="opportunityName"
				isMarginLeftUnset={true}
				disabled={isPreviousCompleted}
			/>
			<Field name="opportunityDescription">
				{({ form, field }: FieldProps) => (
					<div className={styles.dialog__form_item}>
						<label className={styles.label} htmlFor="description">
							Description:
						</label>
						<Editor
							fieldValue={field.value}
							onChange={value => form.setFieldValue('opportunityDescription', value)}
							onBlur={() => form.setFieldTouched(field.name, true)}
							placeholder="Description"
							isShowToolbar={false}
							isOpportunity={true}
						/>
						{errors.opportunityDescription && touched.opportunityDescription && (
							<InputErrorHint errorText={errors.opportunityDescription} disableMargin={true} />
						)}
					</div>
				)}
			</Field>
			<HashtagField
				isVolunteer={true}
				options={options}
				isVisibleHashtagField={isVisibleHashtagField}
				handleChangeVisibleHashtag={handleChangeVisibleHashtag}
			/>
			<Field name="filePathes" label="Photos:" isDisableHeight={true} component={ItemGallery} />
			<div className={styles.form__pickers}>
				<div className={styles.form__pickers_date}>
					<Field
						name="startDate"
						label="Date:"
						component={DatePicker}
						disableFuture={isPreviousCompleted}
						availableFuture={true}
						availablePast={true}
					/>
				</div>
				<div className={styles.form__pickers_time}>
					<Field name="endTime" label="Total hours:" component={TimeLimit} />
				</div>
			</div>
			{isLoading ? (
				<Loader />
			) : (
				<ButtonDefault
					submitType={true}
					classList={['primary', 'lg', 'center']}
					title="Submit"
					disabled={!dirty}
				/>
			)}
		</Form>
	);
};
